import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Text, Spinner, Inputs, Buttons } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { history } from 'Store';
import { routeStateSelector } from 'Store/modules/router/selectors';
import { useTranslation } from 'Hooks/useTranslation';
import { useGetPlanFlexInfo } from 'Containers/Result/hooks';
import { useSavePayLaterXml, useSubmitUserXml } from 'Containers/ResultXml/hooks';
import PayLaterInfoContainer from 'Containers/Result/components/PayLaterInfoContainer';
import CustomTransition from 'Components/CustomTransition';
import Trans from 'Components/Trans';
import Reviews from 'Components/Reviews';
import TwoColLayout from 'Components/Layouts/TwoColLayout';
import { routes } from 'Constants/routes';
import { TAX_YEAR } from 'Config';
import { FRONT_PLANS } from 'Constants/plans';
import * as Styled from './styles';

const FLEX_TYPE = 'flex';

const PaymentLaterXml = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.RESULT);
  const [payLater, setPayLater] = useState(false);
  const paymentLaterProps = useSelector(routeStateSelector);
  const { mutate, isSuccess } = useSavePayLaterXml();
  const { data, isLoading: loading } = useGetPlanFlexInfo();
  const isFlexCheckout = true;
  const { mutate: submitUser, isSuccess: isSuccessSubmitUser } = useSubmitUserXml({
    isFlexCheckout,
  });

  useEffect(() => {
    if (isSuccess) {
      submitUser({ clabe: '', recordId: paymentLaterProps?.recordId });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isSuccessSubmitUser) {
      history.push(`${routes.FILING}?plans=${FRONT_PLANS.FLEX}`, {
        taxReturnType: paymentLaterProps?.taxReturnType,
        taxReturnResult: paymentLaterProps?.taxReturnResult,
      });
    }
  }, [isSuccessSubmitUser]);

  useEffect(() => {
    if (
      !paymentLaterProps ||
      !paymentLaterProps.taxReturnType ||
      !paymentLaterProps.taxReturnResult
    ) {
      history.push(routes.RESULT_XML);
    }
  }, [paymentLaterProps]);

  const handleClickSaveCard = (): void => {
    mutate({
      taxYear: TAX_YEAR.toString(),
      type: FLEX_TYPE,
    });
  };

  return (
    <TwoColLayout>
      <CustomTransition start>
        {!data || loading ? (
          <Styled.SpinnerContainer justify="center" align="center">
            <Spinner size="M" justify="center" align="center" />
          </Styled.SpinnerContainer>
        ) : (
          <Styled.Container
            direction="column"
            align="center"
            justify="center"
            gap="dimension_24"
            fullHeight
          >
            <Text type="paragraphs" color="dark" size="L_L_S">
              {t('Result.payLater.title', {
                context: !paymentLaterProps?.isFlexNotMandatory && 'notMandatory',
              })}
            </Text>
            {data && <PayLaterInfoContainer data={data} />}
            <Styled.ButtonsContainer direction="column" fullWidth gap="dimension_24">
              <Styled.InputContainer>
                <Inputs.CheckBox
                  id="pay-later-checkbox"
                  value={payLater}
                  onChange={value => setPayLater(value)}
                  label={t('Result.paylater.price')}
                />
              </Styled.InputContainer>
              <Buttons.Classic
                disabled={!payLater}
                testID="payLater"
                mode="primary"
                size="m"
                onClick={() => handleClickSaveCard()}
                label={t('Result.CTA.paylater')}
              />
              <Reviews />
              <Text type="paragraphs" size="XS_S_S" color="grey">
                <Trans
                  i18nKey="Result.paylater.conditions"
                  ns="result"
                  components={{
                    a: <Styled.Link target="_blank" />,
                  }}
                />
              </Text>
            </Styled.ButtonsContainer>
          </Styled.Container>
        )}
      </CustomTransition>
    </TwoColLayout>
  );
};

export default PaymentLaterXml;
